import React from 'react';
import { useHistory } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Mail, MapPin, MoreVertical, Phone, UserRound, Users } from 'lucide-react';
import LoadingDataUi from '@app/components/loading';
import { CUSTOMERS } from '@app/pages/ROUTECONSTS';

const ROLE_STYLES = {
  admin: 'bg-[#549A9A]/10 text-[#2F6F6F]',
  staff: 'bg-[#F3F4F6] text-[#4B5563]'
};

const initials = (user) =>
  `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || '?';

const location = (staff) =>
  [staff.city, staff.state, staff.country].filter(Boolean).join(', ') || '—';

const StaffTable = ({ staff, loading, isFiltered, onAdd }) => {
  const history = useHistory();

  if (loading) {
    return (
      <div className="py-10">
        <LoadingDataUi />
      </div>
    );
  }

  if (staff.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <div className="grid place-items-center w-12 h-12 rounded-full bg-[#549A9A]/10 text-[#549A9A]">
          <Users size={22} />
        </div>
        <p className="mt-4 text-[16px] font-semibold text-[#1F2937]">
          {isFiltered ? 'No matching staff' : 'No staff yet'}
        </p>
        <p className="mt-1 text-[14px] text-[#6B7280]">
          {isFiltered
            ? 'Try a different search term or role filter.'
            : 'Add your first staff member to give them dashboard access.'}
        </p>
        {!isFiltered && (
          <button
            onClick={onAdd}
            className="mt-5 h-[40px] px-5 rounded-[10px] bg-[#549A9A] hover:bg-[#447f7f] text-[#E2FFEE] text-[14px] font-semibold transition-colors"
          >
            Add Staff
          </button>
        )}
      </div>
    );
  }

  const openProfile = (member) => history.push(`${CUSTOMERS}/${member.user._id}`);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse">
        <thead className="bg-[#F9F9FB]">
          <tr>
            {['Member', 'Phone', 'Location', 'Role', ''].map((head, idx) => (
              <th
                key={idx}
                className="text-left py-3 px-4 text-[#6B7280] text-[13px] font-medium uppercase tracking-wide"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {staff.map((member) => (
            <tr
              key={member._id}
              onClick={() => openProfile(member)}
              className="border-t border-[#E9E9E9] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
            >
              <td className="px-4 py-3">
                <div className="flex gap-3 items-center">
                  <div className="grid place-items-center w-10 h-10 shrink-0 rounded-full bg-[#549A9A] text-white text-[14px] font-semibold">
                    {initials(member.user)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-[#1F2937] capitalize truncate">
                      {member.user?.first_name} {member.user?.last_name}
                    </p>
                    <p className="flex gap-1 items-center text-[13px] text-[#6B7280] truncate">
                      <Mail size={12} />
                      {member.user?.email}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-[14px] text-[#4B5563]">
                <span className="flex gap-1.5 items-center">
                  <Phone size={14} className="text-[#9CA3AF]" />
                  {member.phone || '—'}
                </span>
              </td>
              <td className="px-4 py-3 text-[14px] text-[#4B5563]">
                <span className="flex gap-1.5 items-center">
                  <MapPin size={14} className="text-[#9CA3AF]" />
                  {location(member)}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-[12px] font-medium capitalize ${
                    ROLE_STYLES[member.role] || ROLE_STYLES.staff
                  }`}
                >
                  {member.role}
                </span>
              </td>
              <td
                className="px-4 py-3 text-right"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      aria-label="Staff actions"
                      className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F3F4F6] focus:outline-none"
                    >
                      <MoreVertical size={16} />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="end"
                      sideOffset={4}
                      className="z-50 min-w-[160px] p-1 bg-white rounded-lg border border-[#E9E9E9] shadow-lg"
                    >
                      <DropdownMenu.Item
                        onSelect={() => openProfile(member)}
                        className="flex gap-2 items-center px-2.5 py-2 text-[14px] text-[#374151] rounded-md cursor-pointer outline-none data-[highlighted]:bg-[#F3F4F6]"
                      >
                        <UserRound size={14} />
                        View profile
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StaffTable;
